import { Box } from "@mui/material";
import WatchOutlined from "@mui/icons-material/WatchOutlined";
import React, { useLayoutEffect, useRef, useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import {
  cardZone,
  eventCalories,
  eventDisplayName,
  formatSessionCount,
  formatSessionCountShort,
} from "../../helpers/activitySelectors";
import { ActivityEvent } from "../../model/workoutPlanTypes";
import WorkoutTypeIcon from "../shared/WorkoutTypeIcon";

type Props = {
  event: ActivityEvent;
  /** Drawn with the primary border while its details are shown beside the week. */
  selected?: boolean;
  onOpen: (event: ActivityEvent) => void;
};

/**
 * A wearable-recorded activity of a day, after the mobile `ActivityCard` for
 * an unscheduled entry: the type glyph on a light disc, the name with its
 * session count and the "Tracker" mark at the right, then what was
 * recorded — calories, duration and the zone when the entry carries one.
 * Picking it selects it for the details pane.
 */
const TrackedActivityCard = ({ event, selected = false, onOpen }: Props) => {
  const theme: Theme = useTheme();
  const name = eventDisplayName(event);
  const sessions = formatSessionCount(event);
  const zone = cardZone(event);

  // When "Name (4 Sessions)" doesn't fit, drop to "Name (4 Sess.)" before the name
  // itself truncates. The full width is remembered at the switch, so the label
  // comes back once the title is wide enough again.
  const titleRef = useRef<HTMLDivElement>(null);
  const fullWidthRef = useRef(0);
  const compactRef = useRef(false);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el || !sessions) return;
    const fit = () => {
      let next = compactRef.current;
      if (!next && el.scrollWidth > el.clientWidth) {
        fullWidthRef.current = el.scrollWidth;
        next = true;
      } else if (next && el.clientWidth >= fullWidthRef.current) {
        next = false;
      }
      compactRef.current = next;
      setCompact(next);
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [name, sessions]);
  const sessionsLabel = compact ? formatSessionCountShort(event) : sessions;

  const metrics = [
    `${Math.round(eventCalories(event))} Kcal`,
    event.duration_in_minutes
      ? `${Math.round(event.duration_in_minutes)} min`
      : "",
    zone !== undefined ? `Zone ${zone}` : "",
  ].filter(Boolean);

  return (
    <Box
      sx={styles.card(theme, selected)}
      onClick={() => onOpen(event)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === "Enter" || keyEvent.key === " ") {
          keyEvent.preventDefault();
          onOpen(event);
        }
      }}
    >
      <Box sx={styles.titleRow}>
        <Box sx={styles.iconDisc(theme)}>
          <WorkoutTypeIcon
            type={event.type}
            fill={theme.colors_ds.NEUTRAL_TEXT_STRONG}
          />
          {![1, 2, 3, 4].includes(event.type) && (
            <WatchOutlined
              sx={{ fontSize: 14, color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
            />
          )}
        </Box>
        <Box
          ref={titleRef}
          sx={styles.title(theme)}
          title={`${name} ${sessions}`.trim()}
        >
          {name}
          {sessionsLabel && (
            <Box component="span" sx={styles.sessions(theme)}>
              {" "}
              {sessionsLabel}
            </Box>
          )}
        </Box>
        <Box sx={styles.tracker(theme)}>Tracker</Box>
      </Box>
      <Box sx={styles.metrics(theme)}>
        {metrics.map((metric) => (
          <Box key={metric} component="span">
            {metric}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TrackedActivityCard;

const styles = {
  card: (theme: Theme, selected: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    padding: "16px 12px",
    borderRadius: "16px",
    border: `1px solid ${
      selected ? theme.colorTokens.P[60] : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: selected
      ? theme.colorTokens.P[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    "&:hover": { borderColor: theme.colorTokens.P[60] },
    "&:focus-visible": {
      outline: `2px solid ${theme.colorTokens.P[60]}`,
      outlineOffset: "2px",
    },
  }),
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  iconDisc: (theme: Theme) => ({
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
  }),
  title: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  sessions: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
  }),
  tracker: (theme: Theme) => ({
    flexShrink: 0,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  // "286 Kcal   21 min   Zone 0", muted like the mobile's unscheduled description.
  metrics: (theme: Theme) => ({
    display: "flex",
    gap: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
